/**
 * novel-visibility-display — 四档可见受众在 admin **界面上**的唯一展示口径。
 *
 * 判据本身（闭集、三层解析 `resolveEffectiveVisibility`、列表合成 `listDisplayVisibility`）一律取
 * `@novel-admin/shared`，本文件**不重新实现任何判据**，只承载它们的中文标签 / chip 色 / 说明文案，
 * 以及「这一层有没有自己的覆盖」「值来自哪一层」两组界面语。
 *
 * 契约：`03-数据字典/20-内容.md` §6（四档语义与发现面 / 直连面分表）/ §6.1（具名规则集，明令
 * 消费面引用规则名、禁止内联判据串）；`02-API契约/A6-书籍域.md` §11（`source_layer`）/ §15.1
 * （列表展示合成口径）/ §15.2 · §15.3（详情回显层② 原值、不合成）。
 */

import { type NovelVisibility, listDisplayVisibility } from '@novel-admin/shared';

export interface VisibilityMeta {
  /** chip / 下拉里的短标签 */
  label: string;
  /** 受众口径全称（§6 档位列的括注），用于下拉副标题与 title */
  audience: string;
  /** 一句话说明：发现面 + 直连面各自的受众（§6 两列合并成一句） */
  desc: string;
  /** 设计系统 chip 类名 */
  chipClass: string;
  /** 状态点色 token（与 chipClass 同源，避免两处各判各的色） */
  dot: string;
}

/**
 * 四档展示元信息（唯一一份）。键的闭集由 `NovelVisibility` 类型钉死 —— shared 加档位而这里没跟进
 * 会直接 tsc 报错，不会静默漏一档。
 */
export const VISIBILITY_META: Record<NovelVisibility, VisibilityMeta> = {
  PUBLIC: {
    label: '公开',
    audience: '普通用户可见',
    desc: '全体用户（含游客）可见 · 进书城 / 搜索 / 推荐等发现面',
    chipClass: 'chip-pos',
    dot: 'var(--pos)',
  },
  PAYING_ONLY: {
    label: '付费可见',
    // 档名说的是**发现面**受众，不是「仅已付费用户可读」（§6 关键差异第一条明写这一句）
    audience: '付费用户可见',
    desc: '仅已付费用户进发现面 · 直连面全体可达（深链 / 历史 / 收藏，含游客）',
    chipClass: 'chip-info',
    dot: 'var(--info)',
  },
  UNLOCKED_ONLY: {
    label: '已购可见',
    audience: '解锁用户可见',
    desc: '不进发现面（不新增曝光）· 已购用户仍可经历史 / 收藏 / 深链直连阅读',
    chipClass: 'chip-warn',
    dot: 'var(--warn)',
  },
  DELISTED: {
    label: '下架',
    audience: '全局下架',
    desc: '合规强制下架 · 发现面与直连面全拒（含已付费 / 已购 / 已买断）· 必须填下架原因',
    chipClass: 'chip-neg',
    dot: 'var(--neg)',
  },
};

/**
 * 改档二次确认的后果描述（§6 发现面 / 直连面语义落到运营语）。
 *
 * 与 `VISIBILITY_META.desc` 分开：那份是 chip / 下拉的一行提示，这份回答「改了会怎样」。
 * 但两份都是**同一套档位语义**的界面投影，故同住本文件 —— 它们分居两处时，契约一改就只改得动
 * 一边（`PAYING_ONLY` 直连面放开那次就是两处各说各话）。
 *
 * `DELISTED` 不在本表：下架走独立的填原因弹窗，不复用这条确认文案。
 */
export const VISIBILITY_CONFIRM_MESSAGE: Record<Exclude<NovelVisibility, 'DELISTED'>, string> = {
  PUBLIC: '这一本语言版对全体用户（含游客）可见，进书城 / 搜索 / 推荐。只作用于这一本语言书。',
  // 本档**只收窄发现面**：直连面对全体放开（含游客），运营常误以为它同时挡住了阅读入口 ——
  // 这条文案是改档前唯一的提示位，必须把「挡的是曝光、不是访问」说清楚（字典 §6 关键差异第一条）
  PAYING_ONLY:
    '仅在本 App 有过成功支付的用户能在书城 / 搜索 / 推荐里看到；但不挡直连 —— 投放深链 / 书架历史 / 收藏进来的用户（含游客）照常能打开这本书，未解锁章节仍按原价计费。已解锁过的用户也不失去已得权益。',
  UNLOCKED_ONLY:
    '不再进书城 / 搜索 / 推荐（商业隐藏），已购用户仍可从书架 / 历史 / 深链继续阅读已购章节。',
};

/** 界面枚举顺序 = §6 的受众单调收窄次序（`PUBLIC ⊇ PAYING_ONLY ⊇ UNLOCKED_ONLY ⊇ ∅`）。 */
export const VISIBILITY_OPTIONS: NovelVisibility[] = [
  'PUBLIC',
  'PAYING_ONLY',
  'UNLOCKED_ONLY',
  'DELISTED',
];

export function visibilityLabel(v: NovelVisibility): string {
  return VISIBILITY_META[v].label;
}

export function visibilityChipClass(v: NovelVisibility): string {
  return VISIBILITY_META[v].chipClass;
}

export function visibilityDot(v: NovelVisibility): string {
  return VISIBILITY_META[v].dot;
}

/** 层② 写表单里「不覆盖、跟随书组默认层」这一选项的界面语（对应请求体 `visibility: null`）。 */
export const INHERIT_LABEL = '继承书组默认';
export const INHERIT_DESC =
  '本语言版不单独配置，跟随书组默认层变动 —— 与显式配「公开」不同：后者钉死为全体可见，书组默认层改了也不跟随';

/**
 * `BookAppStatusResponse.apps[].source_layer` → 界面语（A6 §11）。运营据此判断该改哪一层。
 */
export const SOURCE_LAYER_LABEL: Record<'APP' | 'NOVEL' | 'GROUP_DEFAULT', string> = {
  APP: '本 App 覆盖',
  NOVEL: '本语言版覆盖',
  GROUP_DEFAULT: '书组默认',
};

export const SOURCE_LAYER_HINT: Record<'APP' | 'NOVEL' | 'GROUP_DEFAULT', string> = {
  APP: '层③：该 App 有自己的覆盖行，改这一行只影响这个 App',
  NOVEL: '层②：本语言版配了单书覆盖，改它对本书全部投放 App 生效',
  GROUP_DEFAULT: '层①：本行未覆盖、书组默认层直接生效，改它对全书组全部语言版本生效',
};

/**
 * 发现面触达（字典 §6.1「可曝光」规则在**档位维度**上的投影）。
 *
 * 「可曝光」= `PUBLIC` 放行 / `PAYING_ONLY` 且已付费放行 / 其余一律拒。**已付费是 per-user 判据**，
 * admin 后台没有用户上下文、求不出来，故本函数只回答档位那一半：`PAYING_ONLY` 记为 `PAYING`
 * （部分触达）而不是笼统的「可见」或「不可见」—— 把它归任一侧都会误导运营。
 */
export type DiscoveryReach = 'ALL' | 'PAYING' | 'NONE';

export function discoveryReach(v: NovelVisibility): DiscoveryReach {
  if (v === 'PUBLIC') return 'ALL';
  if (v === 'PAYING_ONLY') return 'PAYING';
  return 'NONE';
}

export const DISCOVERY_REACH_LABEL: Record<DiscoveryReach, string> = {
  ALL: '正常曝光',
  PAYING: '仅付费用户曝光',
  NONE: '不进发现面',
};

/**
 * 列表 / 卡片上「一行的受众档」展示所需的全部信息（A6 §15.1 展示口径 + 覆盖来源）。
 *
 * `value` 恒为合成值（`listDisplayVisibility`，层② 覆盖层①），**不是层② 裸值** —— 层② 为 `null`
 * 是常态（建书与建语言版落库即 `NULL`），直接渲染裸值会让这些行的受众档列空白，而它们恰恰能被
 * 走同一 `COALESCE` 的筛选命中。`overridden` 另外告诉运营该改哪一层。
 */
export interface VisibilityDisplay {
  value: NovelVisibility;
  /** 层② 是否有自己的覆盖（`novels.visibility !== null`）；false = 继承层① */
  overridden: boolean;
}

export function visibilityDisplayOf(
  novelOverride: NovelVisibility | null,
  groupDefault: NovelVisibility,
): VisibilityDisplay {
  return {
    value: listDisplayVisibility(novelOverride, groupDefault),
    overridden: novelOverride !== null,
  };
}

/** 覆盖来源后缀（列表 chip 旁的小字 / title）：区分「这行自己配的」与「跟着书组默认」。 */
export function overrideOriginLabel(overridden: boolean): string {
  return overridden ? '本语言版覆盖' : '继承书组默认';
}
